import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Plus, Trash, ChatDots, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { title: "", body: "", tags: "" };

export default function CannedResponses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/canned-responses").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ title: c.title, body: c.body, tags: (c.tags || []).join(", ") }); setOpen(true); };

  const submit = async () => {
    setSaving(true);
    const payload = { title: form.title, body: form.body, tags: form.tags.split(",").map(s => s.trim()).filter(Boolean) };
    try {
      if (editId) { await api.put(`/canned-responses/${editId}`, payload); toast.success("Template updated"); }
      else { await api.post("/canned-responses", payload); toast.success("Template saved"); }
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => { if (!window.confirm("Delete this template?")) return; await api.delete(`/canned-responses/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="p-6" data-testid="canned-view">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Response Templates</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">Canned Responses</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Reusable reply templates for agents. Use placeholders <code className="font-mono text-primary">{"{{customer}}"}</code>, <code className="font-mono text-primary">{"{{ticket_number}}"}</code>, <code className="font-mono text-primary">{"{{agent_name}}"}</code> — auto-filled when inserted.</p>
        </div>
        <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditId(null); setForm(EMPTY); } }}>
          <SheetTrigger asChild><Button data-testid="new-canned-btn" onClick={openNew} className="rounded-sm font-mono uppercase text-xs"><Plus size={14} className="mr-2" />New Template</Button></SheetTrigger>
          <SheetContent className="bg-background border-border w-full sm:max-w-lg">
            <SheetHeader><SheetTitle className="font-display">{editId ? "Edit Canned Response" : "New Canned Response"}</SheetTitle></SheetHeader>
            <div className="space-y-4 mt-6">
              <div><Label className="text-xs uppercase">Title</Label><Input data-testid="canned-title" className="mt-1 rounded-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label className="text-xs uppercase">Body</Label><Textarea data-testid="canned-body" className="mt-1 rounded-sm min-h-[180px] font-mono text-sm" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
              <div><Label className="text-xs uppercase">Tags (comma-separated)</Label><Input data-testid="canned-tags" className="mt-1 rounded-sm font-mono" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
              <Button data-testid="canned-save" onClick={submit} disabled={!form.title || !form.body || saving} className="w-full rounded-sm font-mono uppercase text-xs">{saving ? "Saving…" : (editId ? "Save Changes" : "Save Template")}</Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map(c => (
          <Card key={c.id} className="p-5 border-border rounded-sm" data-testid={`canned-card-${c.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2"><ChatDots size={14} className="text-primary" /><div className="font-display text-lg">{c.title}</div></div>
              <div className="flex items-center gap-2">
                <button data-testid={`canned-edit-${c.id}`} onClick={() => openEdit(c)} className="text-muted-foreground hover:text-primary" title="Edit"><PencilSimple size={14} /></button>
                <button data-testid={`canned-del-${c.id}`} onClick={() => del(c.id)} className="text-muted-foreground hover:text-red-700 dark:hover:text-red-400" title="Delete"><Trash size={14} /></button>
              </div>
            </div>
            <pre className="whitespace-pre-wrap font-mono text-xs text-zinc-600 dark:text-zinc-300 line-clamp-6 mb-2">{c.body}</pre>
            <div className="flex gap-1 flex-wrap">{c.tags?.map(t => <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 border border-border">{t}</span>)}</div>
          </Card>
        ))}
        {items.length === 0 && <div className="text-muted-foreground">No templates yet.</div>}
      </div>
    </div>
  );
}
